import { computed, useContext } from "@nuxtjs/composition-api";
import { sharedRef, useVSFContext, Logger } from "@vue-storefront/core";
import { COOKIES } from "~/helpers/constants";
import { userCheckoutStore } from "~/stores/checkout-store";
import { getCMSLabels } from "~/helpers/globalLabels";

const useSapcc = (id: string) => {
  const genericError = getCMSLabels("genericerror");
  // Loads context used to call API endpoint
  const context = useVSFContext();
  const { error: nuxtError, i18n } = useContext();
  // Shared ref holding the response from the API
  const result = sharedRef(null, `auth-${id}`);
  // Shared ref indicating whether any method is waiting for the data from the API
  const loading = sharedRef(false, `auth-loading-${id}`);
  // Shared ref holding errors from the methods
  const error = sharedRef({ auth: null }, `auth-error-${id}`);
  // Shared ref holding sapcctoken
  const sapccToken = sharedRef<string>("", COOKIES.SAPCC_TOKEN);
  const useCheckoutStore = userCheckoutStore();
  const getUserID = () => {
    const useCookieData = context.$cookies.get(COOKIES.USER_DATA);
    return useCookieData ? useCookieData.u : "anonymous";
  };

  const baseStoreId = i18n.localeProperties.baseStoreId;
  const country =
    i18n.localeProperties.code === "int"
      ? ""
      : i18n.localeProperties.baseStoreCode;
  const appKey = i18n.localeProperties.appKey;
  const cookiesToBeRemoved = [
    `${appKey}#oi`,
    `${appKey}#oc`,
    `${appKey}#ci`,
    `${appKey}#cGUID`,
    "vsf-cart-main",
    `${appKey}kopt`,
    `${appKey}pinit`,
    `${appKey}iOr`,
    `${appKey}iOc`,
    `${appKey}iOp`,
    `${appKey}iOt`,
  ];
  let lang = i18n.localeProperties.languageCode;
  const locale = i18n.localeProperties.code
    ? lang
    : "en" + "-" + country
    ? country
    : "uk";

  const keepOnPaymentTab = computed(
    () => !!context.$cookies.get(`${appKey}kopt`)
  );

  // SAPCC Token generation
  const load = async () => {
    try {
      loading.value = true;
      // check if token is already generated, if not generate new one
      if (sapccToken.value == "") {
        const response = await context.$vsf.$sapccvolatile.api.auth();
        // store new generated token to VSF universal cookie
        context.$cookies.set(COOKIES.SAPCC_TOKEN, response, {
          maxAge: 60 * 60 * 9, // set expiry to 9 hours as returned from Auth
          path: "/",
          secure: true,
          httpOnly: true,
        });
        sapccToken.value = response;
        result.value = sapccToken.value;
      } else {
        result.value = sapccToken.value;
      }
    } catch (err) {
      error.value.auth = err;
      Logger.error(`Auth Error ${error.value.auth}`);
    } finally {
      loading.value = false;
    }
  };

  // Create new fresh shopping cart
  const loadShoppingCart = async (
    params?: {
      deliveryModeType: String;
      keepAnonymousCart: String;
      oldCartId: String;
    },
    forceNewCart = false
  ) => {
    Logger.debug(`useSapcc/${id}/loadShoppingCart`, params ?? "");
    try {
      //commented because I'm not 100% that we don't need this
      // if (keepOnPaymentTab.value) {
      //   return;
      // }

      if (sapccToken.value == "") await load();

      loading.value = true;
      const userUID = await getUserID();
      const cartMaxAge =
        userUID !== "anonymous" ? 60 * 60 * 24 * 30 : 60 * 60 * 24;

      // Token validation
      if (sapccToken.value == "") {
        error.value = {
          status: 401,
          message: genericError,
        };
      }
      // proceed further
      else {
        let existingLocale = context.$cookies.get(appKey + "#locale"); //TBC
        let isLocaleSwitched = false;

        if (
          existingLocale === undefined ||
          existingLocale === "" ||
          existingLocale !== locale
        ) {
          context.$cookies.set(appKey + "#locale", locale);
          isLocaleSwitched = true;
        }

        const existingCartId = forceNewCart
          ? undefined
          : context.$cookies.get(appKey + "#ci");
        const existingCartUID = context.$cookies.get(appKey + "#cUID");

        // create new cart, as it's no more valid

        if (
          existingCartId === undefined ||
          existingCartId === "" ||
          isLocaleSwitched ||
          (existingCartUID &&
            existingCartUID !== userUID &&
            userUID !== "anonymous")
        ) {
          const shoppingCartRes =
            await context.$vsf.$sapccvolatile.api.createCart({
              userId: userUID,
              lang,
              country,
              keepAnonymousCart: false, //params?.keepAnonymousCart ?? "",
              oldCartId:
                existingCartUID !== userUID && userUID !== "anonymous"
                  ? existingCartId
                  : "",
              token: sapccToken.value,
              deliveryModeType:
                existingCartUID !== userUID && userUID !== "anonymous"
                  ? useCheckoutStore.getDeliveryModeType
                  : "",
              baseStoreId,
            });

          if (shoppingCartRes?.errors) {
            //if fails to create new fresh cart, retry
            error.value =
              shoppingCartRes.errors.length > 0
                ? shoppingCartRes.errors[0]
                : shoppingCartRes.errors;

            const errorType = error.value.type;

            if (errorType === "CartError") {
              // cleanup everything and create new fresh cart
              cookiesToBeRemoved.forEach((key) => context.$cookies.remove(key));
              useCheckoutStore.resetAllData(appKey, false); // keep user data

              await loadShoppingCart(undefined, true);
          } else {
              error.value = {
                status: 403,
                message: genericError,
              };
            }
          } else {
            // Set cart cookies based on SAPCC default cart expiry (Guest: 24 hours | LoggedIn : 30 days )
            context.$cookies.set(
              appKey + "#ci",
              userUID !== "anonymous"
                ? shoppingCartRes.code
                : shoppingCartRes.guid, // cart code for logged-in users, GUID for guest users
              {
                maxAge: cartMaxAge,
                path: "/",
              }
            );

            // Set a separate cookie for the cart GUID (used for cart tracking)
            context.$cookies.set(appKey + "#cGUID", shoppingCartRes.guid, {
              maxAge: cartMaxAge,
              path: "/", // Cookie is accessible throughout the site
            });

            // Set the main cart identifier cookie (vsf-cart-main)
            context.$cookies.set(
              "vsf-cart-main",
              userUID !== "anonymous"
                ? shoppingCartRes.code
                : shoppingCartRes.guid, // Cart code for logged-in users, GUID for guest users
              {
                maxAge: cartMaxAge,
                path: "/",
              }
            );

            // Add this cookie  in case of Anonymous user
            (userUID === "anonymous" && existingCartUID === undefined) ||
            existingCartUID === ""
              ? context.$cookies.set(appKey + "#cUID", userUID, {
                  maxAge: 60 * 60 * 24,
                  path: "/",
                })
              : userUID !== "anonymous" && existingCartUID
              ? context.$cookies.remove(appKey + "#cUID")
              : " ";

            result.value = shoppingCartRes;

            // update checkout store cart on each loadShoppingCart
            useCheckoutStore.setCurrentCart(result.value);

            if (
              shoppingCartRes?.deliveryMode &&
              userUID === "anonymous" &&
              existingCartUID === undefined
            ) {
              const deliveryObj = shoppingCartRes?.deliveryMode;
              Object.assign(deliveryObj, {
                deliveryType: shoppingCartRes?.deliveryModeType,
              });

              // store preffered delivery mode on create new cart to checkout store
              useCheckoutStore.setSelectedDeliveryMode(deliveryObj);
            }
          }
        }
      }
    } catch (err) {
      error.value.api = err;
      Logger.error(`loadShoppingCart Error ==>>  ${error.value.api}`);
      return { errorMessage: error.value.api };
    } finally {
      loading.value = false;
    }
  };

  // Get the shopping cart
  const getShoppingCart = async (params?: {
    deliveryModeType: String;
    keepAnonymousCart: String;
    oldCartId: String;
  }) => {
    Logger.debug(`useSapcc/${id}/getShoppingCart`, params ?? "");

    try {
      if (sapccToken.value == "") await load();
      loading.value = true;
      const userUID = await getUserID();

      if (sapccToken.value == "") {
        error.value = {
          status: 401,
          message: genericError,
        };
      } else {
        let existingLocale = context.$cookies.get(appKey + "#locale"); //TBC

        if (
          existingLocale === undefined ||
          existingLocale === "" ||
          existingLocale !== locale
        ) {
          context.$cookies.set(appKey + "#locale", locale);
        }

        const existingCartId =
          userUID === "anonymous"
            ? context.$cookies.get(appKey + "#cGUID")
            : context.$cookies.get(appKey + "#ci");



        if (existingCartId) {
          //previous cart exists, retrieving it
          await prepareGetCartCall({
            cartId: existingCartId,
            userId: userUID,
            lang,
            country,
          });
        } else {
          // create new shopping cart
          await loadShoppingCart();

          // note: pass the latest created cart id as per user current state intead retrieving it from removed cookies due to cart not found error
          await prepareGetCartCall({
            cartId:
              userUID === "anonymous" ? result.value.guid : result.value.code,
            userId: userUID,
            lang,
            country,
          });
        }
        // }
      }
    } catch (err) {
      error.value.api = err;
      Logger.error(`getShoppingCart Error ==>>  ${error.value.api}`);
      return { errorMessage: error.value.api };
    } finally {
      loading.value = false;
    }
  };

  // get cart call prepare
  const prepareGetCartCall = async (params: {
    cartId: any;
    userId: String;
    lang: String;
    country: String;
  }) => {
    const shoppingCartRes = await context.$vsf.$sapccvolatile.api.getCart({
      userId: params.userId,
      cartGuid: params.cartId,
      lang: params.lang,
      country: params.country,
      token: sapccToken.value,
      baseStoreId,
    });
    if (shoppingCartRes?.errors) {
      //access token error
      error.value =
        shoppingCartRes.errors.length > 0
          ? shoppingCartRes.errors[0]
          : shoppingCartRes.errors;
      const errorType = error.value.type;

      if (errorType === "CartError") {
        cookiesToBeRemoved.forEach((key) => context.$cookies.remove(key));
        useCheckoutStore.resetAllData(appKey, false); // keep user data

        // Create new fresh Cart
        await loadShoppingCart(undefined, true);
      } else {
        error.value = {
          status: 403,
          message: genericError,
        };
      }

      // Object.assign(error.value, { status: 403 });
    } else {
      result.value = shoppingCartRes;
      useCheckoutStore.removeAllProductsInCart(appKey);

      // Save the most recently retrieved cart to the checkout store for local processing, since we're not invoking loadShoppingCart here.
      useCheckoutStore.setCurrentCart(result.value);

      if (shoppingCartRes?.entries) {
        shoppingCartRes.entries?.map((p) => {
          useCheckoutStore.setProductsInCart({ appKey, pCode: p.product.code });
        });
        useCheckoutStore.setCartItemsCount({
          appKey,
          itemCount: shoppingCartRes.entries.map(it => it.quantity).reduce((a, b) => a + b, 0)
        });
      } else {
        Object.assign(shoppingCartRes, { entries: [] });
      }
    }
  };
  // Add product to the shopping cart
  const addToShoppingCart = async (params: {
    code: String;
    qty: String;
    customizationCost: String;
    recipeId: String;
    primaryImageUrl: String;
    type: String;
    shortDescription: String;
    detailedDescription: String;
    price: String;
    message: String;
    deliveryDate: String;
  }) => {
    Logger.debug(`useSapcc/${id}/addToShoppingCart`, params);

    try {
      if (sapccToken.value == "") await load();
      loading.value = true;
      const userUID = await getUserID();
      const cartMaxAge =
        userUID !== "anonymous" ? 60 * 60 * 24 * 30 : 60 * 60 * 24;
      const cartGuid = context.$cookies.get(appKey + "#ci");

      if (sapccToken.value == "") {
        error.value = {
          status: 401,
          message: genericError,
        };
      } else {
        const body =
          !params.type &&
          !params.shortDescription &&
          !params.detailedDescription &&
          !params.price &&
          !params.message &&
          !params.deliveryDate
            ? {}
            : {
                type: params.type,
                shortDescription: params.shortDescription,
                detailedDescription: params.detailedDescription,
                price: params.price,
                message: params.message,
                deliveryDate: params.deliveryDate,
              };

        const shoppingCartRes = await context.$vsf.$sapccvolatile.api.addToCart(
          {
            userId: userUID,
            cartGuid,
            code: params.code,
            qty: params.qty,
            customizationCost: params.customizationCost ?? "",
            recipeId: params.recipeId ?? "",
            primaryImageUrl: params.primaryImageUrl ?? "",
            body: body,
            token: sapccToken.value,
            baseStoreId,
          }
        );

        // check if any cartError, if not return generic error
        if (shoppingCartRes?.errors) {
          error.value =
            shoppingCartRes.errors.length > 0
              ? shoppingCartRes.errors[0]
              : shoppingCartRes.errors;

          const errorType = error.value.type;

          if (errorType === "CartError") {
            // cleanup everything and create new fresh cart

            cookiesToBeRemoved.forEach((key) => context.$cookies.remove(key));
            useCheckoutStore.resetAllData(appKey, false); // keep user data

            await loadShoppingCart(undefined, true);
            await addToShoppingCart(params);
          } else {
            error.value = {
              status: 403,
              message: genericError,
            };
          }
        } else {
          // update cart item count to store, if successfully added to cart
          error.value = {};
          if (!context.$cookies.get(appKey + "#ci")) {
            context.$cookies.set(appKey + "#ci", cartGuid, {
              maxAge: cartMaxAge,
              path: "/",
            });
          }

          useCheckoutStore.setProductsInCart({ appKey, pCode: params.code });
          useCheckoutStore.setCartItemsCount({
            appKey,
            itemCount: shoppingCartRes.totalUnitCount
          });

          result.value = shoppingCartRes;
          Object.assign(result.value, { status: 200 });
        }
      }
    } catch (err) {
      error.value.api = err;
      Logger.error(`addtoShoppingCart Error ==>>  ${error.value.api}`);
      return { errorMessage: error.value.api };
    } finally {
      loading.value = false;
    }
  };

  // Remove all products from the shopping cart
  const removeFromShoppingCart = async (params: { entryNumber: String }) => {
    Logger.debug(`useSapcc/${id}/removeFromShoppingCart`, params);

    try {
      if (sapccToken.value == "") await load();
      loading.value = true;
      const userUID = await getUserID();
      const cartGuid = context.$cookies.get(appKey + "#ci");

      if (sapccToken.value === null) {
        error.value = {
          status: 401,
          message: genericError,
        };
      } else {
        const shoppingCartRes =
          await context.$vsf.$sapccvolatile.api.removeFromCart({
            userId: userUID,
            cartGuid,
            entryNumber: params.entryNumber,
            token: sapccToken.value,
            baseStoreId,
          });

        if (shoppingCartRes?.errors) {
          //access token error
          error.value =
            shoppingCartRes.errors.length > 0
              ? shoppingCartRes.errors[0]
              : shoppingCartRes.errors;
          Object.assign(error.value, { status: 403 });
        } else {
          useCheckoutStore.removeProductsInCart({
            appKey,
            pCode: shoppingCartRes?.removedCartEntry?.product?.code,
          });
           useCheckoutStore.setCartItemsCount({
            appKey,
            itemCount: shoppingCartRes?.totalUnitCount ?? 0
          });
          result.value = shoppingCartRes;
        }
      }
    } catch (err) {
      error.value.api = err;
      Logger.error(`removeFromShoppingCart Error ==>>  ${error.value.api}`);
      return { errorMessage: error.value.api };
    } finally {
      loading.value = false;
    }
  };

  const updateShoppingCart = async (params: {
    qty: Number;
    entryNumber: Number;
  }) => {
    Logger.debug(`useSapcc/${id}/updateShoppingCart`, params);

    try {
      if (sapccToken.value == "") await load();
      loading.value = true;
      const userUID = await getUserID();
      const cartGuid = context.$cookies.get(appKey + "#ci");
      error.value = null;

      if (sapccToken.value === null) {
        error.value = {
          status: 401,
          message: genericError,
        };
      } else {
        const shoppingCartRes =
          await context.$vsf.$sapccvolatile.api.updateCart({
            userId: userUID,
            cartGuid,
            qty: params.qty,
            entryNumber: params.entryNumber,
            token: sapccToken.value,
            baseStoreId,
          });

        if (shoppingCartRes?.errors) {
          //access token error
          error.value =
            shoppingCartRes.errors.length > 0
              ? shoppingCartRes.errors[0]
              : shoppingCartRes.errors;
          Object.assign(error.value, { status: 403 });
        } else {
          result.value = shoppingCartRes;
          useCheckoutStore.setCartItemsCount({
            appKey,
            itemCount: shoppingCartRes?.totalUnitCount ?? 0
          });
        }
      }
    } catch (err) {
      error.value.api = err;
      Logger.error(`updateShoppingCart Error ==>>  ${error.value.api}`);
      return { errorMessage: error.value.api };
    } finally {
      loading.value = false;
    }
  };

  const getShoppingCartEntries = async () => {
    Logger.debug(`useSapcc/${id}/getShoppingCartEntries`);

    try {
      if (sapccToken.value == "") await load();
      loading.value = true;
      const userUID = await getUserID();
      const cartGuid = context.$cookies.get(appKey + "#ci");

      if (sapccToken.value === null) {
        error.value = {
          status: 401,
          message: genericError,
        };
      } else {
        const shoppingCartRes =
          await context.$vsf.$sapccvolatile.api.getCartEntries({
            userId: userUID,
            cartGuid,
            token: sapccToken.value,
            baseStoreId,
          });

        if (shoppingCartRes?.errors) {
          //access token error
          error.value =
            shoppingCartRes.errors.length > 0
              ? shoppingCartRes.errors[0]
              : shoppingCartRes.errors;
          Object.assign(error.value, { status: 403 });
        } else {
          result.value = shoppingCartRes;
        }
      }
    } catch (err) {
      error.value.api = err;
      Logger.error(`getShoppingCartEntries Error ==>>  ${error.value.api}`);
      return { errorMessage: error.value.api };
    } finally {
      loading.value = false;
    }
  };

  // Clear the cart.
  const clearShoppingCart = async (params) => {
    return {};
  };

  // Apply Promo Code to shopping cart
  const applyShoppingCartVoucher = async (params: { voucherId: String }) => {
    Logger.debug(`useSapcc/${id}/applyShoppingCartVoucher`, params);

    try {
      if (sapccToken.value == "") await load();
      loading.value = true;
      const userUID = await getUserID();
      const cartGuid = context.$cookies.get(appKey + "#ci");

      if (sapccToken.value === null) {
        error.value = {
          status: 401,
          message: genericError,
        };
        return { errorMessage: error.value.message };
      } else {
        const shoppingCartRes =
          await context.$vsf.$sapccvolatile.api.applyCartVoucher({
            userId: userUID,
            cartGuid,
            voucherId: params.voucherId,
            token: sapccToken.value,
            baseStoreId,
          });

        if (shoppingCartRes?.errors) {
          //access token error
          error.value =
            shoppingCartRes.errors.length > 0
              ? shoppingCartRes.errors[0]
              : shoppingCartRes.errors;
          Object.assign(error.value, { status: 403 });
          return { errorMessage: error.value.message || error.value };
        } else {
          result.value = shoppingCartRes;
          return { result: result.value };
        }
      }
    } catch (err) {
      error.value.api = err;
      Logger.error(`applyShoppingCartVoucher Error ==>>  ${error.value.api}`);
      return { errorMessage: error.value.api };
    } finally {
      loading.value = false;
    }
  };

  // Remove Promo Code from shopping cart
  const removeShoppingCartVoucher = async (params: { voucherId: String }) => {
    Logger.debug(`useSapcc/${id}/removeShoppingCartVoucher`, params);

    try {
      if (sapccToken.value == "") await load();
      loading.value = true;
      const userUID = await getUserID();
      const cartGuid = context.$cookies.get(appKey + "#ci");

      if (sapccToken.value === null) {
        error.value = {
          status: 401,
          message: genericError,
        };
      } else {
        const shoppingCartRes =
          await context.$vsf.$sapccvolatile.api.removeCartVoucher({
            userId: userUID,
            cartGuid,
            voucherId: params.voucherId,
            token: sapccToken.value,
            baseStoreId,
          });

        if (shoppingCartRes?.errors) {
          //access token error
          error.value =
            shoppingCartRes.errors.length > 0
              ? shoppingCartRes.errors[0]
              : shoppingCartRes.errors;
          Object.assign(error.value, { status: 403 });
        } else {
          result.value = shoppingCartRes;
        }
      }
    } catch (err) {
      error.value.api = err;
      Logger.error(`removeShoppingCartVoucher Error ==>>  ${error.value.api}`);
      return { errorMessage: error.value.api };
    } finally {
      loading.value = false;
    }
  };

  const getShoppingCartVouchers = async () => {
    Logger.debug(`useSapcc/${id}/getShoppingCartVouchers`);

    try {
      if (sapccToken.value == "") await load();
      loading.value = true;
      const userUID = await getUserID();
      const cartGuid = context.$cookies.get(appKey + "#ci");

      if (sapccToken.value === null) {
        error.value = {
          status: 401,
          message: genericError,
        };
      } else {
        const shoppingCartRes =
          await context.$vsf.$sapccvolatile.api.getCartVouchers({
            userId: userUID,
            cartGuid,
            code: "test",
            token: sapccToken.value,
            baseStoreId,
          });

        if (shoppingCartRes?.errors) {
          //access token error
          error.value =
            shoppingCartRes.errors.length > 0
              ? shoppingCartRes.errors[0]
              : shoppingCartRes.errors;
          Object.assign(error.value, { status: 403 });
        } else {
          result.value = shoppingCartRes;
        }
      }
    } catch (err) {
      error.value.api = err;
      Logger.error(`getShoppingCartVouchers Error ==>>  ${error.value.api}`);
      return { errorMessage: error.value.api };
    } finally {
      loading.value = false;
    }
  };

  // Apply gift wrap to product in shopping cart
  const applyGiftWrap = async (params: { entryNumber: String }) => {
    Logger.debug(`useSapcc/${id}/applyGiftWrap`, params);

    try {
      if (sapccToken.value == "") await load();
      loading.value = true;
      const userUID = await getUserID();
      const cartGuid = context.$cookies.get(appKey + "#ci");
      const giftWrapDetails = {
        code: "moomin-giftwrap",
        type: "GIFT_WRAPPING",
      };

      if (sapccToken.value === null) {
        error.value = {
          status: 401,
          message: genericError,
        };
      } else {
        const shoppingCartRes =
          await context.$vsf.$sapccvolatile.api.addServiceProductEntry({
            userId: userUID,
            cartGuid,
            entryNumber: params.entryNumber,
            body: giftWrapDetails,
            token: sapccToken.value,
            baseStoreId,
          });

        if (shoppingCartRes?.errors) {
          //access token error
          error.value =
            shoppingCartRes.errors.length > 0
              ? shoppingCartRes.errors[0]
              : shoppingCartRes.errors;
          Object.assign(error.value, { status: 403 });
        } else {
          result.value = shoppingCartRes;
        }
      }
    } catch (err) {
      error.value.api = err;
      Logger.error(`applyGiftWrap Error ==>>  ${error.value.api}`);
      return { errorMessage: error.value.api };
    } finally {
      loading.value = false;
    }
  };

  // Remove gift wrap from product in shopping cart
  const removeGiftWrap = async (params: { entryNumber: String }) => {
    Logger.debug(`useSapcc/${id}/removeGiftWrap`, params);

    try {
      if (sapccToken.value == "") await load();
      loading.value = true;
      const userUID = await getUserID();
      const cartGuid = context.$cookies.get(appKey + "#ci");
      const serviceProductCode = "moomin-giftwrap"; // Can be moved to constants

      if (sapccToken.value === null) {
        error.value = {
          status: 401,
          message: genericError,
        };
      } else {
        const shoppingCartRes =
          await context.$vsf.$sapccvolatile.api.removeServiceProductEntry({
            userId: userUID,
            cartGuid,
            entryNumber: params.entryNumber,
            serviceProductCode,
            token: sapccToken.value,
            baseStoreId,
          });

        if (shoppingCartRes?.errors) {
          //access token error
          error.value =
            shoppingCartRes.errors.length > 0
              ? shoppingCartRes.errors[0]
              : shoppingCartRes.errors;
          Object.assign(error.value, { status: 403 });
        } else {
          result.value = shoppingCartRes;
        }
      }
    } catch (err) {
      error.value.api = err;
      Logger.error(`removeGiftWrap Error ==>>  ${error.value.api}`);
      return { errorMessage: error.value.api };
    } finally {
      loading.value = false;
    }
  };

  // Fetch Product Price Inventory
  const fetchProductPriceInventory = async (params) => {
    try {
      Logger.debug(`useSapcc/${id}/fetchProductPriceInventory`, params);
      if (sapccToken.value === "") {
        await load();
      }

      loading.value = true;

      if (sapccToken.value === null) {
        return nuxtError({ statusCode: 403 });
      }

      result.value =
        await context.$vsf.$sapccvolatile.api.fetchProductPriceInventory({
          productCode: params.productCode,
          token: sapccToken.value,
          baseStoreId,
          country,
        });

      if (result.value?.errors) {
        error.value = {
          status: 404,
          message: genericError,
        };
        // Handle the errors, but don't return an error response here
        // Instead, you could set an appropriate flag or state to handle this scenario
      }
    } catch (err) {
      Logger.error(`get product inventory error ==>>  ${err}`);
      // You can log the error here, but don't return a nuxtError immediately
    } finally {
      loading.value = false;
    }
  };

  const createOrder = async () => {
    Logger.debug(`useSapcc/${id}/createOrder`);

    const userUID = await getUserID();

    const cartGuid = computed(() => context.$cookies.get(appKey + "#ci"));
    const orderGuid = computed(() => {
      return userUID == "anonymous"
        ? context.$cookies.get(appKey + "#oi")
        : context.$cookies.get(appKey + "#oc");
    });

    // Check if order already created
    if (orderGuid.value) {
      try {
        const order = await context.$vsf.$sapccvolatile.api.getOrder({
          userId: userUID,
          lang,
          token: sapccToken.value,
          orderId: orderGuid.value,
          baseStoreId,
        });

        // Assuming `result` is a reactive object, you can directly assign the `order` to `result.value`
        result.value = order;
      } catch (error) {
        // Handle the error here (e.g., show a notification, log the error, etc.)
      }
    } else {
      try {
        const order = await context.$vsf.$sapccvolatile.api.createOrder({
          userId: userUID,
          cartGuid: cartGuid.value,
          urlPrefix: "JDE",
          token: sapccToken.value,
          baseStoreId,
        });

        // Store cart id in cookies if not already set
        if (!order?.errors) {
          context.$cookies.set(appKey + "#toi", order.guid, {
            maxAge: 60 * 60 * 24 * 15,
            path: "/",
          });
          context.$cookies.set(appKey + "#toc", order.code, {
            maxAge: 60 * 60 * 24 * 15,
            path: "/",
          });
          // Assuming `result` is a reactive object, you can directly assign the `order` to `result.value`
          result.value = order;
        } else {
          throw new Error(genericError);
        }
      } catch (error) {
        // Handle the error here (e.g., show a notification, log the error, etc.)
        console.error("Error creating order:", error);
        error.value = {
          status: 401,
          message: error.message,
        };
      }
    }
  };

  const cancelOrder = async (params) => {
    try {
      Logger.debug(`useSapcc/${id}/cancelOrder`);

      if (sapccToken.value === "") {
        await load();
      }

      loading.value = true;

      let userUID = await getUserID();
      const isGuestUser = userUID === null || userUID === "anonymous";

      const orderCode = context.$cookies.get(`${appKey}#oc`);

      if (!userUID || isGuestUser) {
        userUID = encodeURIComponent(params.userId);
      }

      if (sapccToken.value === null) {
        throw new Error(genericError);
      }

      const order = await context.$vsf.$sapccvolatile.api.cancelOrder({
        userId: userUID,
        orderCode,
        recreateCart: params.recreateCart,
        token: sapccToken.value,
        baseStoreId,
      });

      context.$cookies.remove(`${appKey}#oi`);
      context.$cookies.remove(`${appKey}#oc`);
      context.$cookies.set(
        `${appKey}#ci`,
        isGuestUser ? order.guid : order.code
      );
      context.$cookies.set(
        `${appKey}#cUID`,
        isGuestUser ? order.guid : order.code
      );
      context.$cookies.set(`${appKey}#cGUID`, order.guid);
      context.$cookies.set(
        "vsf-cart-main",
        isGuestUser ? order.guid : order.code
      );

      result.value = order;
    } catch (err) {
      error.value = {
        status: 401,
        message: genericError,
      };
      console.error("Error cancelling order:", err);
    } finally {
      loading.value = false;
    }
  };

  return {
    load, // access token
    loadShoppingCart,
    getShoppingCart,
    addToShoppingCart,
    removeFromShoppingCart,
    updateShoppingCart,
    getShoppingCartEntries,
    applyShoppingCartVoucher,
    removeShoppingCartVoucher,
    getShoppingCartVouchers,
    fetchProductPriceInventory,
    applyGiftWrap,
    removeGiftWrap,
    createOrder,
    cancelOrder,
    result: computed(() => result.value),
    loading: computed(() => loading.value),
    error: computed(() => error.value),
  };
};

export default useSapcc;
